import React, { useRef, useState } from 'react';
import { apiFetch } from '../api';


function formatBytes(value) {
  const bytes = Number(value || 0);
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString()} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}


export default function SourceControl({ project, onSourceChanged }) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const source = project?.source;

  if (!source) return null;

  const replace = async file => {
    if (!file || working) return;
    const confirmed = window.confirm(
      `Replace “${source.original_filename}” with “${file.name}”?\n\n` +
      'The new file will be validated first. After a successful replacement, field mappings, rationalization decisions, relationships, and exports must be reviewed again. The current version will remain available to restore.'
    );
    if (!confirmed) return;
    setWorking(true);
    setError(null);
    const body = new FormData();
    body.append('file', file);
    try {
      const response = await apiFetch('/upload-source-csv', { method: 'POST', body });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'The source could not be replaced.');
      onSourceChanged(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setWorking(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const restore = async () => {
    if (working) return;
    if (!window.confirm('Restore the previous source version and its saved migration work? The current version will become the available prior revision.')) return;
    setWorking(true);
    setError(null);
    try {
      const response = await apiFetch('/source/restore-previous', { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'The previous version could not be restored.');
      onSourceChanged(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="border-b border-slate-200 bg-white px-6 py-3">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="font-semibold text-slate-800">Source export</span>
            <span className="truncate text-slate-600">{source.original_filename}</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">{formatBytes(source.size_bytes)}</span>
          </div>
          <div className="mt-0.5 text-xs text-slate-500">A replacement is validated before it becomes active.</div>
          {error && <div className="mt-2 text-sm text-red-700" role="alert">{error}</div>}
        </div>
        <div className="flex shrink-0 gap-2">
          {project.has_previous_revision && (
            <button disabled={working} onClick={restore} className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-45">Restore previous</button>
          )}
          <label className={`rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 ${working ? 'cursor-not-allowed opacity-45' : 'cursor-pointer'}`}>
            {working ? 'Validating…' : 'Replace source'}
            <input ref={inputRef} className="hidden" type="file" accept=".csv,.xlsx" disabled={working} onChange={event => replace(event.target.files[0])} />
          </label>
        </div>
      </div>
    </div>
  );
}
